import { BrandLogo } from "@/components/shared/BrandLogo";
import { useState, useEffect } from "react";
import { API, axios } from "@/api";
import {
  Eye, Heart, ClipboardCheck, AlertTriangle, TrendingUp,
  Loader2,
} from "lucide-react";

const FunnelBar = ({ label, value, max, color }) => {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div className="flex items-center gap-2">
      <span className="text-[10px] text-slate-400 w-12 text-right font-medium">{label}</span>
      <div className="flex-1 h-4 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs font-bold text-slate-600 w-8">{value}</span>
    </div>
  );
};

const IntelRow = ({ item, showFriction }) => (
  <div className="flex items-center gap-3 py-2.5 border-b border-slate-50 last:border-0" data-testid={`intel-row-${item.business_id}`}>
    <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
      <BrandLogo name={item.name} logoUrl={item.logo_url} size="sm" />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-xs font-semibold text-slate-700 truncate">{item.name}</p>
      <div className="flex items-center gap-2 mt-0.5">
        <span className="text-[10px] text-slate-400"><Eye className="w-3 h-3 inline mr-0.5" />{item.views}</span>
        <span className="text-[10px] text-pink-400"><Heart className="w-3 h-3 inline mr-0.5" />{item.favorites}</span>
        <span className="text-[10px] text-emerald-500"><ClipboardCheck className="w-3 h-3 inline mr-0.5" />{item.claims}</span>
      </div>
    </div>
    {showFriction ? (
      <div className="flex-shrink-0 px-2 py-1 rounded-lg bg-red-50 border border-red-200">
        <p className="text-[10px] font-bold text-red-600">{item.friction_score}</p>
        <p className="text-[8px] text-red-400">friction</p>
      </div>
    ) : (
      <div className={`flex-shrink-0 px-2 py-1 rounded-lg ${item.view_to_claim_pct > 0 ? "bg-emerald-50 border border-emerald-200" : "bg-slate-50 border border-slate-200"}`}>
        <p className={`text-[10px] font-bold ${item.view_to_claim_pct > 0 ? "text-emerald-600" : "text-slate-400"}`}>{item.view_to_claim_pct}%</p>
        <p className={`text-[8px] ${item.view_to_claim_pct > 0 ? "text-emerald-400" : "text-slate-300"}`}>converts</p>
      </div>
    )}
  </div>
);

export default function OfferIntelPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("top");

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/offer-intelligence`);
      setData(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  if (loading) return <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-300" /></div>;
  if (!data) return <p className="text-xs text-slate-400 py-4">Failed to load intelligence data</p>;

  const { summary, top_viewed, friction_alerts, best_converters } = data;
  const maxViews = top_viewed[0]?.views || 1;

  const tabs = [
    { key: "top", label: "Top Viewed", count: summary.offers_with_views },
    { key: "friction", label: "Friction", count: summary.high_friction_count },
    { key: "converters", label: "Best Converters", count: best_converters.length },
  ];

  const activeList = tab === "top" ? top_viewed : tab === "friction" ? friction_alerts : best_converters;

  return (
    <div className="space-y-4" data-testid="offer-intel-panel">
      {/* Summary stats */}
      <div className="grid grid-cols-3 gap-2">
        <div className="bg-sky-50 rounded-xl p-3 text-center border border-sky-100">
          <p className="text-lg font-bold text-sky-700">{summary.total_views}</p>
          <p className="text-[10px] text-sky-500">Total Views</p>
        </div>
        <div className="bg-emerald-50 rounded-xl p-3 text-center border border-emerald-100">
          <p className="text-lg font-bold text-emerald-700">{summary.avg_view_to_claim_pct}%</p>
          <p className="text-[10px] text-emerald-500">View→Claim</p>
        </div>
        <div className="bg-red-50 rounded-xl p-3 text-center border border-red-100">
          <p className="text-lg font-bold text-red-700">{summary.high_friction_count}</p>
          <p className="text-[10px] text-red-500">High Friction</p>
        </div>
      </div>

      {/* Funnel visualization */}
      <div className="bg-slate-50 rounded-xl p-3 space-y-2">
        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Global Funnel</p>
        <FunnelBar label="Views" value={summary.total_views} max={summary.total_views} color="bg-sky-400" />
        <FunnelBar label="Favs" value={top_viewed.reduce((a, b) => a + b.favorites, 0)} max={summary.total_views} color="bg-pink-400" />
        <FunnelBar label="Tracked" value={top_viewed.reduce((a, b) => a + b.tracks, 0)} max={summary.total_views} color="bg-amber-400" />
        <FunnelBar label="Claimed" value={summary.total_claims} max={summary.total_views} color="bg-emerald-500" />
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 rounded-xl p-1">
        {tabs.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex-1 py-2 text-xs font-semibold rounded-lg transition-all flex items-center justify-center gap-1 ${
              tab === t.key ? "bg-white text-slate-800 shadow-sm" : "text-slate-400"
            }`}
            data-testid={`intel-tab-${t.key}`}
          >
            {t.label}
            <span className={`text-[9px] px-1.5 rounded-full ${tab === t.key ? "bg-slate-100" : "bg-slate-200/50"}`}>{t.count}</span>
          </button>
        ))}
      </div>

      {/* List */}
      <div className="max-h-80 overflow-y-auto">
        {activeList.length === 0 ? (
          <p className="text-xs text-slate-400 text-center py-6">No data yet</p>
        ) : (
          activeList.map(item => (
            <IntelRow key={item.business_id} item={item} showFriction={tab === "friction"} />
          ))
        )}
      </div>

      {/* Friction explanation */}
      {tab === "friction" && friction_alerts.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-[10px] text-red-600 leading-relaxed">
            High friction = lots of views but few/no claims. These offers are generating interest but something is blocking conversion. Check: signup requirements, app downloads, minimum purchase, or confusing terms.
          </p>
        </div>
      )}
    </div>
  );
}
